import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui-hud";
import { Lock, Plane, Users2, Briefcase, ShieldCheck, ArrowRight, Radar, Sparkles } from "lucide-react";

/**
 * MembershipPreview
 *
 * "See what you're getting" — anonymous-visitor snapshot shown on the pricing page.
 * Displays 5 redacted operator cards + 3 redacted job posts + live stats. Every
 * card is deliberately blurred with a lock overlay + "Sign up to unlock" CTA so
 * prospects hit the paywall having already fallen for the product.
 */
export default function MembershipPreview() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/marketing/preview").then((r) => setData(r.data)).catch(() => setData({ operators: [], jobs: [], stats: {} }));
  }, []);

  if (!data) return null;
  const { operators = [], jobs = [], stats = {} } = data;

  return (
    <section className="py-16 border-t border-white/[0.05]" data-testid="membership-preview">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="mono text-[10px] tracking-widest text-cyan mb-2 flex items-center justify-center gap-2">
            <Sparkles className="h-3 w-3" /> // LIVE PREVIEW · MEMBERS-ONLY CONTENT
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-bold mb-3 tracking-tight">See what you're getting.</h2>
          <p className="text-white/60 max-w-2xl mx-auto text-sm md:text-base">
            {stats.members || 0}+ pilots, operators &amp; mechanics · {stats.operators || 0} vetted operators ·{" "}
            {stats.open_contracts || 0} open contracts · {stats.aircraft_for_sale || 0} jets for sale — all inside.
          </p>
        </div>

        {/* Operators */}
        <div className="mb-12">
          <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
            <div>
              <div className="overline text-cyan/80">// GATED OPERATOR DIRECTORY</div>
              <div className="font-display text-2xl font-bold">Charter operators you'll unlock.</div>
            </div>
            <div className="mono text-[10px] text-white/40">Names + tail #s + contacts revealed after sign-up.</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {operators.map((op) => <OperatorPreviewCard key={op.id} op={op} />)}
            {/* Ghost card teasing the count */}
            <Link
              to="/register"
              data-testid="preview-more-operators"
              className="rounded-lg border border-dashed border-cyan/40 bg-cyan/[0.03] p-5 flex flex-col items-center justify-center text-center hover:bg-cyan/[0.06] transition-colors group"
            >
              <Lock className="h-5 w-5 text-cyan mb-2 group-hover:scale-110 transition-transform" />
              <div className="mono text-[10px] tracking-widest text-cyan">// +{Math.max(0, (stats.operators || 78) - operators.length)} MORE</div>
              <div className="text-sm text-white/80 mt-1">Full directory unlocks after sign-up</div>
              <div className="mono text-[10px] text-cyan mt-2 flex items-center gap-1">JOIN PJP <ArrowRight className="h-3 w-3" /></div>
            </Link>
          </div>
        </div>

        {/* Jobs */}
        <div className="mb-6">
          <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
            <div>
              <div className="overline text-orange-hud/90">// LIVE JOB BOARD</div>
              <div className="font-display text-2xl font-bold">Contracts posted this week.</div>
            </div>
            <div className="mono text-[10px] text-white/40">Employer + pay range revealed inside.</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {jobs.map((j) => <JobPreviewCard key={j.id} job={j} />)}
            {jobs.length === 0 && (
              <div className="col-span-3 rounded-lg border border-white/10 bg-white/[0.02] p-6 text-center text-sm text-white/50">
                No open contracts right now — but members average 4-6 new posts per week.
              </div>
            )}
          </div>
        </div>

      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
function OperatorPreviewCard({ op }) {
  return (
    <div
      className="relative rounded-lg border border-white/10 bg-black/40 p-4 overflow-hidden group hover:border-cyan/30 transition-colors"
      data-testid={`preview-op-${op.id}`}
    >
      {/* Lock ribbon */}
      <div className="absolute top-3 right-3 flex items-center gap-1 mono text-[9px] px-2 py-0.5 rounded-full bg-cyan/10 border border-cyan/30 text-cyan">
        <Lock className="h-2.5 w-2.5" /> MEMBERS ONLY
      </div>

      <div className="flex items-center gap-3 mb-3">
        <div className="h-11 w-11 rounded-lg bg-white/[0.04] grid place-items-center border border-white/10">
          <Plane className="h-4 w-4 text-cyan" />
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="font-display text-base font-semibold truncate"
            style={{ filter: "blur(4px) opacity(0.9)", userSelect: "none" }}
          >
            {op.redacted_name}
          </div>
          <div className="mono text-[10px] text-white/50 mt-0.5">
            {op.fleet_count > 0 ? `${op.fleet_count} tail${op.fleet_count === 1 ? "" : "s"} · ` : ""}
            {op.primary_bases_redacted?.slice(0, 2).join(" · ") || "Multiple bases"}
          </div>
        </div>
      </div>

      {/* Real (non-redacted) fleet types — that's the "value proof" */}
      {op.aircraft_types_operated?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {op.aircraft_types_operated.slice(0, 4).map((t) => (
            <span key={t} className="mono text-[9px] px-1.5 py-0.5 rounded-full bg-cyan/10 border border-cyan/30 text-cyan">{t}</span>
          ))}
        </div>
      )}

      {/* Regions */}
      {op.service_regions?.length > 0 && (
        <div className="text-[11px] text-white/60 leading-relaxed">
          <span className="text-white/40 mono text-[9px] tracking-widest mr-1">FLIES</span>
          {op.service_regions.slice(0, 3).join(" · ")}
        </div>
      )}

      {/* Safety rating badge */}
      {op.argus_wyvern_rating && op.argus_wyvern_rating !== "None" && (
        <div className="mt-3 inline-flex items-center gap-1 mono text-[9px] px-2 py-0.5 rounded-full bg-avail/10 border border-avail/30 text-avail">
          <ShieldCheck className="h-2.5 w-2.5" /> {op.argus_wyvern_rating}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
function JobPreviewCard({ job }) {
  const posted = job.posted_at ? timeAgo(new Date(job.posted_at)) : "recent";
  // Stable pseudo-random pay range keyed off job.id so it doesn't flicker on re-render.
  const seed = (job.id || "").split("").reduce((s, c) => s + c.charCodeAt(0), 0);
  const fakePay = 500 + (seed % 700);
  return (
    <div className="relative rounded-lg border border-white/10 bg-black/40 p-4 group hover:border-orange-hud/30 transition-colors" data-testid={`preview-job-${job.id}`}>
      <div className="absolute top-3 right-3 flex items-center gap-1 mono text-[9px] px-2 py-0.5 rounded-full bg-orange-hud/10 border border-orange-hud/30 text-orange-hud">
        <Lock className="h-2.5 w-2.5" /> APPLY INSIDE
      </div>

      <div className="flex items-center gap-2 mb-2">
        <Briefcase className="h-3.5 w-3.5 text-orange-hud" />
        <div className="mono text-[10px] tracking-widest text-white/50 uppercase">{posted}</div>
      </div>
      <div className="font-display text-lg font-bold mb-1">{job.role_needed}</div>
      {job.aircraft_type && (
        <div className="mono text-[11px] text-cyan mb-2">{job.aircraft_type}</div>
      )}
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <div>
          <div className="mono text-[9px] text-white/40 tracking-widest">BASE</div>
          <div className="mono text-white/85" style={{ filter: "blur(3px)", userSelect: "none" }}>{job.base_redacted || "K••B"}</div>
        </div>
        <div>
          <div className="mono text-[9px] text-white/40 tracking-widest">PAY</div>
          <div className="mono text-white/85" style={{ filter: "blur(3px)", userSelect: "none" }}>${fakePay.toLocaleString()}/day</div>
        </div>
      </div>
      <div className="mt-3 mono text-[10px] text-white/40 flex items-center gap-1 border-t border-white/[0.06] pt-2">
        <Users2 className="h-3 w-3" /> Posted by verified operator
      </div>
    </div>
  );
}

function timeAgo(d) {
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}
